/**
 * offlineAuth.ts
 * オフライン認証パックの端末キャッシュ。
 * オンラインログイン成功時に本人の照合材料を保存し、オフライン時のログイン
 * （パスワード照合・ローカルセッション確立）と、復帰時の同期トークン取得に使う。
 *
 * 保存内容（user_id をキー）:
 *  - employee_code / user_name / user_role / company_id : ローカルセッション用
 *  - password_hash : オフラインでのパスワード照合(bcrypt)用（サーバ現行ハッシュ）
 *  - secret        : 復帰時に本人トークンを取得するためのシークレット
 *  - face_descriptor : サーバ保存の顔特徴量（別端末登録分の照合にも使える）
 *
 * ※ 共有端末にこれらを保存することは運用上許容する前提（利用者の指示）。
 * ※ 顔特徴量・パスワードハッシュ等を含むため、ストア全体を AES-GCM で暗号化して
 *   保存する(トライアル覚書 別紙2 第4項(7)対応)。復号済みの内容はメモリに保持し、
 *   既存の同期APIを維持する。初期化(復号)は非同期のため、読み出し前に
 *   offlineAuthReady() を await すること。
 */
import bcrypt from 'bcryptjs';
import { encryptString, decryptString } from './secureStore';

export interface OfflineAuthBundle {
  user_id: string;
  employee_code: string;
  user_name: string;
  user_role: string;
  company_id: string;
  avatar_path?: string | null;
  password_hash?: string;
  secret?: string;
  face_descriptor?: number[] | null;
  updated_at: string;
}

const KEY_LEGACY = 'catms_offline_auth';   // 旧: 平文JSON(読み込み後に削除)
const KEY_V2 = 'catms_offline_auth_v2';    // 新: AES-GCM暗号化

type Store = Record<string, OfflineAuthBundle>;

let store: Store = {};

async function persist(): Promise<void> {
  try {
    localStorage.setItem(KEY_V2, await encryptString(JSON.stringify(store)));
  } catch { /* 保存失敗は無視(次回保存で回復) */ }
}

// モジュール読み込み時に復号してメモリへ展開。旧平文ストアは取り込んで削除する。
const initPromise: Promise<void> = (async () => {
  try {
    const enc = localStorage.getItem(KEY_V2);
    if (enc) {
      const plain = await decryptString(enc);
      if (plain) {
        try { store = JSON.parse(plain) as Store; } catch { store = {}; }
      }
    }
    const legacy = localStorage.getItem(KEY_LEGACY);
    if (legacy) {
      try { store = { ...(JSON.parse(legacy) as Store), ...store }; } catch { /* 壊れた旧データは捨てる */ }
      localStorage.removeItem(KEY_LEGACY);
      await persist();
    }
  } catch {
    store = {};
  }
})();

/** 初期化(暗号化ストアの復号)の完了を待つ。読み出し系APIの前に await すること。 */
export function offlineAuthReady(): Promise<void> {
  return initPromise;
}

/** パックを保存（部分更新可。既存とマージ） */
export function saveOfflineBundle(b: Partial<OfflineAuthBundle> & { user_id: string }): void {
  store[b.user_id] = { ...store[b.user_id], ...b, updated_at: new Date().toISOString() } as OfflineAuthBundle;
  void persist();
}

export function getBundleByEmployeeCode(code: string): OfflineAuthBundle | undefined {
  // オンライン時と同じ扱いにする（全角・空白・大文字小文字を無視）
  const norm = (v: string) => String(v)
    .replace(/[\uFF01-\uFF5E]/g, ch => String.fromCharCode(ch.charCodeAt(0) - 0xFEE0))
    .replace(/[\u3000\s]/g, "")
    .toLowerCase();
  return Object.values(store).find(b => norm(b.employee_code) === norm(code));
}

export function getBundleByUserId(userId: string): OfflineAuthBundle | undefined {
  return store[userId];
}

/** 復帰時の同期トークン取得用: このユーザーのシークレット */
export function getOfflineSecret(userId: string): string | undefined {
  return store[userId]?.secret;
}

/** キャッシュ済み全ユーザー（パスワード変更の自動再取得対象） */
export function listBundles(): OfflineAuthBundle[] {
  return Object.values(store);
}

/**
 * オフラインでのパスワード照合。社員コード＋パスワードが端末キャッシュの
 * bcryptハッシュと一致すれば、そのパックを返す（ローカルログインに使う）。
 */
export function verifyOfflinePassword(employeeCode: string, password: string): OfflineAuthBundle | null {
  const b = getBundleByEmployeeCode(employeeCode);
  if (!b || !b.password_hash) return null;
  try {
    return bcrypt.compareSync(password, b.password_hash) ? b : null;
  } catch {
    return null;
  }
}
